import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import React, { forwardRef, useState, useEffect, useMemo, Children, createRef } from 'react';
import { Fade, Slide } from '@wds/motion';
import { safeRest, KEYS, defaultFonts, useIsomorphicLayoutEffect, isBrowser, usePreviousState, uniqueId, isFocusableElement } from '@wds/utils';
import { createMemoStyles, SIZES } from '@wds/styles';
import { Overlay, CloseButton } from '@wds/internal-components';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var useStyles$3 = createMemoStyles(function (theme) {
  var spacing = theme.spacing;
  return {
    wrenchModalBody: {
      overflowY: 'auto',
      flexGrow: 1,
      padding: function (_a) {
        var padding = _a.padding;
        return padding ? [[spacing[6], spacing[7], spacing[7], spacing[7]]] : undefined;
      }
    }
  };
});

var ModalBody = /*#__PURE__*/forwardRef(function (_a, ref) {
  _a.className;
    var children = _a.children,
    handleClick = _a.handleClick,
    id = _a.id,
    _b = _a.padding,
    padding = _b === void 0 ? true : _b,
    rest = __rest(_a, ["className", "children", "handleClick", "id", "padding"]);
  var wrenchModalBody = useStyles$3({
    padding: padding
  }).wrenchModalBody;
  var handleKeyDown = function handleKeyDown(e) {
    if (e.key === KEYS.ESC) {
      handleClick === null || handleClick === void 0 ? void 0 : handleClick(e);
      e.preventDefault();
    }
  };
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    jsx("section", __assign({
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex: 0,
      ref: ref,
      id: id,
      className: wrenchModalBody,
      onKeyDown: handleKeyDown
    }, safeRest(rest), {
      children: children
    }))
  );
});
ModalBody.displayName = 'ModalBody';

var useStyles$2 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    spacing = theme.spacing;
  return {
    wrenchModalFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      flexShrink: 0,
      padding: spacing[6],
      background: palette.background.light,
      borderTop: "1px solid ".concat(palette.border.muted),
      color: palette.text.dark
    },
    wrenchModalActions: {
      display: 'flex',
      flexGrow: 1,
      flexWrap: 'wrap',
      justifyContent: function (_a) {
        var align = _a.align;
        return align === 'left' ? 'flex-start' : 'flex-end';
      },
      '&:not([class^="wrenchButtonGroup"])': {
        '& > button, & > a': {
          marginLeft: spacing[2]
        }
      }
    }
  };
});

var ModalFooter = function ModalFooter(_a) {
  var _b = _a.align,
    align = _b === void 0 ? 'left' : _b,
    children = _a.children;
  var _c = useStyles$2({
      align: align
    }),
    wrenchModalActions = _c.wrenchModalActions,
    wrenchModalFooter = _c.wrenchModalFooter;
  return jsx("footer", {
    className: wrenchModalFooter,
    children: jsx("div", {
      className: wrenchModalActions,
      children: children
    })
  });
};

var useStyles$1 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    spacing = theme.spacing,
    _a = theme.type,
    fontWeight = _a.fontWeight,
    fontSize = _a.fontSize;
  return {
    wrenchModalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      padding: [spacing[7], spacing[7], 0, spacing[7]],
      background: palette.background.light,
      flexShrink: 0,
      flexDirection: function (_a) {
        var stretched = _a.stretched;
        return stretched ? 'column' : 'row';
      }
    },
    wrenchModalText: {
      fontSize: 24,
      fontWeight: fontWeight.medium,
      color: palette.text.dark,
      marginTop: 0,
      marginBottom: spacing[2]
    },
    wrenchModalTitle: {
      flexGrow: 1,
      width: '100%'
    },
    wrenchModalSubText: {
      fontSize: fontSize.bodySm,
      fontWeight: fontWeight.regular,
      color: palette.text.darkMuted,
      margin: [spacing[1], 0, 0]
    },
    wrenchModalAddOn: {
      flexGrow: 1,
      marginTop: spacing[3],
      width: '100%',
      minWidth: '33%',
      textAlign: 'right',
      // In the case that an addon is the first child (modal title was omitted)
      // we do not want a margin top
      '& :first-child': {
        marginTop: 0
      }
    }
  };
});

var ModalHeader = function ModalHeader(_a) {
  var addOn = _a.addOn,
    header = _a.header,
    id = _a.id,
    subHeader = _a.subHeader,
    _b = _a.stretched,
    stretched = _b === void 0 ? false : _b;
  if (subHeader && addOn) {
    throw new Error('You cannot have both subHeader and addon');
  } else if (!header && subHeader) {
    throw new Error('You must provide a header if you provide a subHeader');
  }
  var _c = useStyles$1({
      stretched: stretched
    }),
    wrenchModalAddOn = _c.wrenchModalAddOn,
    wrenchModalHeader = _c.wrenchModalHeader,
    wrenchModalSubText = _c.wrenchModalSubText,
    wrenchModalText = _c.wrenchModalText,
    wrenchModalTitle = _c.wrenchModalTitle;
  return jsxs("section", {
    className: wrenchModalHeader,
    children: [jsxs("div", {
      className: wrenchModalTitle,
      children: [header && jsx("h2", {
        className: wrenchModalText,
        id: id,
        children: header
      }), subHeader && jsx("p", {
        className: wrenchModalSubText,
        children: subHeader
      })]
    }), addOn && jsx("div", {
      className: wrenchModalAddOn,
      children: addOn
    })]
  });
};

var modalWidth = {
  sm: '443px',
  md: '676px',
  lg: '909px',
  xl: '1300px',
  full: '100%'
};
var useStyles = createMemoStyles(function (theme) {
  var _a;
  var breakpoints = theme.breakpoints,
    palette = theme.palette,
    shadow = theme.shadow,
    spacing = theme.spacing;
  return {
    wrenchModalWrapper: {
      overflow: 'auto',
      position: 'fixed',
      inset: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      pointerEvents: 'none',
      zIndex: function (_a) {
        var elevation = _a.elevation;
        return elevation !== null && elevation !== void 0 ? elevation : theme.elevation.space;
      },
      '& > div': {
        width: function (_a) {
          var size = _a.size;
          return size === 'full' ? '100%' : undefined;
        },
        height: function (_a) {
          var size = _a.size;
          return size === 'full' ? '100%' : undefined;
        }
      }
    },
    wrenchModal: function (_a) {
      var size = _a.size;
      return {
        width: size === 'full' ? '100%' : undefined,
        height: size === 'full' ? '100%' : undefined
      };
    },
    wrenchModalContainer: __assign(__assign({}, defaultFonts(theme)), (_a = {
      boxSizing: 'border-box',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      color: palette.text.dark,
      background: palette.text.light,
      zIndex: function (_a) {
        var elevation = _a.elevation;
        return (elevation !== null && elevation !== void 0 ? elevation : theme.elevation.space) + 1;
      },
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto',
      whiteSpace: 'normal',
      outline: 'none',
      pointerEvents: 'all'
    }, _a["@media (min-width: ".concat(breakpoints.sm, "px)")] = {
      maxWidth: function (_a) {
        var size = _a.size;
        return {
          sm: '80vw',
          md: '80vw',
          lg: '80vw',
          xl: '96vw',
          full: '100vw'
        }[size];
      },
      maxHeight: function (_a) {
        var size = _a.size;
        return {
          sm: '90vh',
          md: '90vh',
          lg: '90vh',
          xl: '96vh',
          full: '100vh'
        }[size];
      },
      borderRadius: function (_a) {
        var fullPage = _a.fullPage;
        return fullPage ? 0 : spacing[4];
      },
      boxShadow: shadow.space,
      position: 'relative',
      top: 'auto',
      left: 'auto',
      right: 'auto',
      bottom: 'auto',
      height: function (_a) {
        var fullPage = _a.fullPage;
        return fullPage ? '100%' : 'auto';
      },
      width: function (_a) {
        var size = _a.size;
        return modalWidth[size];
      }
    }, _a)),
    wrenchModalOverlayContainer: function (_a) {
      var elevation = _a.elevation;
      return {
        zIndex: elevation !== null && elevation !== void 0 ? elevation : theme.elevation.space,
        position: 'relative'
      };
    },
    wrenchModalOverlay: {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      position: 'fixed',
      backgroundColor: palette.background.overlay
    }
  };
});

__spreadArray(__spreadArray([], SIZES.filter(function (x) {
  return x !== 'xs';
}), true), ['full'], false);
var Modal = function Modal(_a) {
  var children = _a.children,
    _b = _a.closeButtonLabel,
    closeButtonLabel = _b === void 0 ? 'Close Modal' : _b,
    _c = _a.dismissible,
    dismissible = _c === void 0 ? true : _c,
    onClose = _a.onClose,
    _d = _a.show,
    show = _d === void 0 ? false : _d,
    _e = _a.size,
    size = _e === void 0 ? 'sm' : _e,
    title = _a.title,
    elevation = _a.elevation,
    _f = _a.role,
    role = _f === void 0 ? 'dialog' : _f,
    _g = _a.alwaysRenderContent,
    alwaysRenderContent = _g === void 0 ? false : _g,
    rest = __rest(_a, ["children", "closeButtonLabel", "dismissible", "onClose", "show", "size", "title", "elevation", "role", "alwaysRenderContent"]);
  var _h = useState(show),
    showWrapper = _h[0],
    setShowWrapper = _h[1];
  useIsomorphicLayoutEffect(function () {
    if (isBrowser) {
      if (show) {
        document.body.style.overflowY = 'hidden';
      } else {
        document.body.style.overflowY = 'auto';
      }
    }
    return function () {
      if (isBrowser) {
        document.body.style.overflowY = 'auto';
      }
    };
  });
  var modalRef = /*#__PURE__*/createRef();
  var modalBodyRef = /*#__PURE__*/createRef();
  var prevShow = usePreviousState(show);
  var fullPage = size === 'full';
  var _j = useStyles({
      size: size,
      elevation: elevation,
      fullPage: fullPage,
      showWrapper: showWrapper
    }),
    wrenchModal = _j.wrenchModal,
    wrenchModalContainer = _j.wrenchModalContainer,
    wrenchModalOverlay = _j.wrenchModalOverlay,
    wrenchModalOverlayContainer = _j.wrenchModalOverlayContainer,
    wrenchModalWrapper = _j.wrenchModalWrapper;
  useEffect(function () {
    if (modalBodyRef.current && prevShow !== show) {
      modalBodyRef.current.focus({
        preventScroll: true
      });
    }
  }, [modalBodyRef, prevShow, show]);
  var titleId = useMemo(function () {
    return uniqueId('modal-title-');
  }, []);
  var bodyId = useMemo(function () {
    return uniqueId('modal-body-');
  }, []);
  var handleClick = function handleClick() {
    if (dismissible) {
      if (onClose) {
        onClose();
      }
    }
  };
  /**
   * ModalHeader exists show aria-labelledby
   * @type {boolean}
   */
  var hasModalHeader = Children.toArray(children).some(function (child) {
    return /*#__PURE__*/React.isValidElement(child) && child.type === ModalHeader;
  });
  var handleKeyDown = function handleKeyDown(event) {
    var _a;
    if (event.key === 'Tab' && modalRef.current) {
      var modalElements = (_a = modalRef === null || modalRef === void 0 ? void 0 : modalRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('*');
      var focusableElements = Array.from(modalElements).filter(function (element) {
        return isFocusableElement(element);
      });
      var firstElement = focusableElements[0];
      var lastElement = focusableElements[focusableElements.length - 1];
      if (isBrowser && focusableElements) {
        if (event.shiftKey && document.activeElement === firstElement) {
          lastElement === null || lastElement === void 0 ? void 0 : lastElement.focus();
          event.preventDefault();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          firstElement === null || firstElement === void 0 ? void 0 : firstElement.focus();
          event.preventDefault();
        }
      }
    }
  };
  if (!showWrapper && show) {
    setShowWrapper(true);
  }
  return jsxs(Fragment, {
    children: [jsx("div", {
      className: fullPage ? '' : wrenchModalOverlayContainer,
      children: jsx(Fade, {
        in: show,
        children: jsx(Overlay, {
          onClick: handleClick,
          className: fullPage ? '' : wrenchModalOverlay
        })
      })
    }), jsx("div", {
      className: wrenchModalWrapper,
      children: jsx(Slide, {
        in: show,
        direction: "up",
        onEnter: function () {
          return setShowWrapper(true);
        },
        onExited: function () {
          return setShowWrapper(false);
        },
        alwaysRenderContent: alwaysRenderContent,
        children: jsx("div", __assign({
          className: wrenchModal
        }, safeRest(rest), {
          children: jsxs("div", {
            className: wrenchModalContainer,
            "aria-label": title,
            "aria-labelledby": hasModalHeader ? titleId : undefined,
            "aria-describedby": bodyId,
            role: role,
            "aria-modal": "true",
            tabIndex: -1,
            onKeyDown: handleKeyDown,
            ref: modalRef,
            children: [dismissible && jsx(CloseButton, {
              variant: "dark",
              onClick: handleClick,
              label: closeButtonLabel
            }), Children.map(children, function (child) {
              if ( /*#__PURE__*/React.isValidElement(child)) {
                var additionalProps = void 0;
                if (child.type === ModalHeader) {
                  additionalProps = {
                    id: titleId
                  };
                } else if (child.type === ModalBody) {
                  additionalProps = {
                    id: bodyId,
                    handleClick: handleClick,
                    ref: modalBodyRef
                  };
                }
                if (additionalProps) {
                  if ( /*#__PURE__*/React.isValidElement(child)) {
                    return /*#__PURE__*/React.cloneElement(child, additionalProps);
                  }
                }
              }
              return child;
            })]
          })
        }))
      })
    })]
  });
};
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
Modal.Header = ModalHeader;

var MODAL_SIZES = ['sm', 'md', 'lg', 'xl', 'full'];

export { MODAL_SIZES, Modal };
